import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import * as zVal from '../../../../lib/validations';
import RadioGroup from '../../../ui/radio-group';
import Button from '../../../ui/button';
import Text from '../../../ui/text';
import Input from '../../../ui/input';
import Select from '../../../ui/select';
import DateInput from '../../../ui/date-input';
import Switch from '../../../ui/switch';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../lib/i18n';
import { authorizedRepresentativeOptions, individualRelationships } from '../constants';

const schema = z.object({
  signature: zVal.textRequired(),
  authorizedRepresentative: zVal.textRequired(),
  representativeFirstName: zVal.textOptionalEmpty(),
  representativeLastName: zVal.textOptionalEmpty(),
  representativeRelationship: zVal.textOptionalEmpty(),
  representativeAddress1: zVal.textOptionalEmpty(),
  representativeAddress2: zVal.textOptionalEmpty(),
  representativeCity: zVal.textOptionalEmpty(),
  representativeState: zVal.textOptionalEmpty(),
  representativeZipCode: zVal.zipCodeOptionalEmpty(),
  representativePhone: zVal.phoneOptionalEmpty(),
  representativeEmail: zVal.emailOptionalEmpty(),
  submittedRepresentativeDocumentation: z.preprocess(
    (val) => (val === undefined ? false : val), 
    z.boolean().optional()
  ),
  individualName: zVal.textOptionalEmpty(),
  individualPhone: zVal.phoneOptionalEmpty(),
  individualRelationship: zVal.textOptionalEmpty(),
  individualSignature: zVal.textOptionalEmpty(),
  individualNPN: zVal.textOptionalEmpty(),
  individualReceivedDate: zVal.textOptionalEmpty(),
}).superRefine((values, context) => {

  if (values.authorizedRepresentative === 'yes') {
    
    const requiredMessage = i18n.t('Required');

    if (!values.representativeFirstName) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeFirstName"],
      });
    }
    if (!values.representativeLastName) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeLastName"],
      });
    }
    if (!values.representativeRelationship) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeRelationship"],
      });
    }
    if (!values.representativeAddress1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeAddress1"],
      });
    }
    if (!values.representativeState) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeState"],
      });
    }
    if (!values.representativeZipCode) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeZipCode"],
      });
    }
    if (!values.representativePhone) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativePhone"],
      });
    }
    if (!values.representativeEmail) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["representativeEmail"],
      });
    }
    if (!values.individualName) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["individualName"],
      });
    }
    if (!values.individualPhone) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["individualPhone"],
      });
    }
    if (!values.individualSignature) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["individualSignature"],
      });
    }
    if (!values.individualReceivedDate) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage,
        path: ["individualReceivedDate"],
      });
    }
  }
  
  return z.NEVER; // The return value is not used, but we need to return something to satisfy the typing
});

function SignatureDetails(props) {
  const { t } = useTranslation();
  const { goToTab, updateData } = props;
  const methods = useForm({
    resolver: zodResolver(schema),
  });
  const {
    register,
    control,
    handleSubmit,
    watch,
    resetField,
    formState: { errors },
  } = methods;

  const watchAuthorizedRepresentative = watch('authorizedRepresentative');

  useEffect(() => {
    if (watchAuthorizedRepresentative === 'yes') {
      resetField('representativeFirstName');
      resetField('representativeLastName');
      resetField('representativeRelationship');
      resetField('representativeAddress1');
      resetField('representativeAddress2');
      resetField('representativeCity');
      resetField('representativeState');
      resetField('representativeZipCode');
      resetField('representativePhone');
      resetField('representativeEmail');
      resetField('individualName');
      resetField('individualPhone');
      resetField('individualRelationship');
      resetField('individualSignature');
      resetField('individualNPN');
      resetField('individualReceivedDate');
    }
  }, [watchAuthorizedRepresentative]);

  const onSubmit = (newData) => {
    updateData(newData);

    goToTab('end-cover');
  };

  return (
    <main className="flex">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <section className="w-full">
            <h2 className="text-2xl font-semibold text-secondary">
              <Text>Important: Read and Sign Below</Text>
            </h2>
            <div className='mt-5'>
              <ul className='list-disc ml-4 text-xl'>
                <li><Text>I must keep both Hospital (Part A) and Medical (Part B) to stay enrolled in Solis Health Plans.</Text></li>
                <li><Text>By joining this Medicare Advantage Plan, I acknowledge that Solis Health Plans will share my information with Medicare, who may use it to track my enrollment, to make payments, and for other purposes allowed by federal law that authorize the collection of this information (see Privacy Act Statement below). Your response to this form is voluntary. However, failure to respond may affect enrollment in the plan.</Text></li>
                <li><Text>I understand that I can be enrolled in only one MA plan at a time, and that enrollment in this plan will automatically end my enrollment in another MA plan. (Exceptions apply for MA PFFS, MA MSA plans.)</Text></li>
                <li><Text>I understand that when my Solis Health Plans coverage begins, I must get all of my medical and prescription drug benefits from Solis Health Plans. Benefits and services provided by Solis Health Plans and contained in my Solis Health Plans “Evidence of Coverage” document (also known as a member contract or subscriber agreement) will be covered. Neither Medicare nor Solis Health Plans will pay for benefits or services that are not covered.</Text></li>
                <li><Text>The information on this enrollment form is correct to the best of my knowledge. I understand that if I intentionally provide false information on this form, I will be disenrolled from the plan.</Text></li>
                <li><Text>I understand that my signature (or the signature of the person legally authorized to act on my behalf) on this application means that I have read and understand the contents of this application. If signed by an authorized representative (as described above), this signature certifies that:</Text>
                  <ol className='list-decimal ml-4 mt-4'>
                    <li><Text>This person is authorized under state law to complete this enrollment, and</Text></li>
                    <li><Text>Documentation of this authority is available upon request by Medicare.</Text></li>
                  </ol>
                </li>
              </ul>
            </div> 
          </section>
          <section className="mt-10 w-full">
            <h2 className="text-2xl font-semibold leading-none text-secondary">
              <Text>Signature *</Text>
            </h2>
            <div className="grid grid-cols-12 gap-5 mt-5">
              <div className='col-span-12 sm:col-span-12 flex flex-col justify-start items-start'>
                <Text type='label' htmlFor='signature' className='mb-5'>By entering my name, I confirm my electronic signature on this form:</Text>
                <Input
                  {...register('signature')}
                  size='sm'
                  label={<Text>Signature</Text>}
                  placeholder="-"
                  errorMessage={errors?.signature?.message || null}
                  isInvalid={errors?.signature ? true : false}
                />
              </div>
              <div className='col-span-12 sm:col-span-12 flex flex-col justify-start items-start'>
                <Text type='label' htmlFor='authorizedRepresentative' className='mb-5'>Authorized Representative? *</Text>
                <RadioGroup
                  control={control}
                  name='authorizedRepresentative'
                  orientation='vertical'
                  size='sm'
                  options={authorizedRepresentativeOptions} 
                  isInvalid={!!errors?.authorizedRepresentative}
                  errorMessage={errors?.authorizedRepresentative?.message || null}
                />
              </div>
            </div> 
            {watchAuthorizedRepresentative === 'yes' && (
              <div>
                <div className='mt-5'>
                  <Text>Please sign the application and provide the details below</Text>
                </div>
                <div className="grid grid-cols-12 gap-5 mt-5">
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeFirstName'>First Name *</Text>
                    <Input
                      {...register('representativeFirstName')}
                      size='sm'
                      label={<Text>First Name</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeFirstName?.message || null}
                      isInvalid={errors?.representativeFirstName ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeLastName'>Last Name *</Text>
                    <Input
                      {...register('representativeLastName')}
                      size='sm'
                      label={<Text>Last Name</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeLastName?.message || null}
                      isInvalid={errors?.representativeLastName ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeRelationship'>Relationship to Enrollee *</Text>
                    <Input
                      {...register('representativeRelationship')}
                      size='sm'
                      label={<Text>Relationship to Enrollee</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeRelationship?.message || null}
                      isInvalid={errors?.representativeRelationship ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeState'>State *</Text>
                    <Input
                      {...register('representativeState')}
                      size='sm'
                      label={<Text>State</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeState?.message || null}
                      isInvalid={errors?.representativeState ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeCity'>City</Text>
                    <Input
                      {...register('representativeCity')}
                      size='sm'
                      label={<Text>City</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeCity?.message || null}
                      isInvalid={errors?.representativeCity ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeZipCode'>Zip Code *</Text>
                    <Input
                      {...register('representativeZipCode')}
                      size='sm'
                      label={<Text>Zip Code</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeZipCode?.message || null}
                      isInvalid={errors?.representativeZipCode ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-6 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeAddress1'>Address 1 *</Text>
                    <Input
                      {...register('representativeAddress1')}
                      size='sm'
                      label={<Text>Address 1</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeAddress1?.message || null}
                      isInvalid={errors?.representativeAddress1 ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-6 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeAddress2'>Address 2</Text>
                    <Input
                      {...register('representativeAddress2')}
                      size='sm'
                      label={<Text>Address 2</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeAddress2?.message || null}
                      isInvalid={errors?.representativeAddress2 ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativePhone'>Phone *</Text>
                    <Input
                      {...register('representativePhone')}
                      size='sm'
                      label={<Text>Phone</Text>}
                      placeholder={t('0123456789')}
                      errorMessage={errors?.representativePhone?.message || null}
                      isInvalid={errors?.representativePhone ? true : false}
                    />
                  </div>
                  <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                    <Text type='label' htmlFor='representativeEmail'>Email *</Text>
                    <Input
                      {...register('representativeEmail')}
                      size='sm'
                      label={<Text>Email</Text>}
                      placeholder="-"
                      errorMessage={errors?.representativeEmail?.message || null}
                      isInvalid={errors?.representativeEmail ? true : false}
                    />
                  </div>
                </div>
                <div className='mt-5'>
                  <Switch name='submittedRepresentativeDocumentation' size='sm' control={control}>
                    <Text className='text-base font-semibold text-gray-600'>I have submitted Authorized Representative documentation with this application.</Text>
                  </Switch>
                </div>
              </div>
            )}      
          </section>
          {watchAuthorizedRepresentative === 'yes' && (<section className="w-full mt-10">
            <h2 className="text-2xl font-semibold text-secondary">
              <Text>For individuals helping enrollee with completing this form only.</Text>
            </h2>
            <div className='mt-3'>
              <Text className='text-base text-gray-600 font-bold'>Complete this section if you’re an individual (i.e. agents, brokers, SHIP counselors, family members, or other third parties) helping an enrollee fill out this form.</Text>
            </div>
            <div>
              <div className="grid grid-cols-12 gap-5 mt-5">
                <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='individualName'>Name *</Text>
                  <Input
                    {...register('individualName')}
                    size='sm'
                    label={<Text>Name</Text>}
                    placeholder="-"
                    errorMessage={errors?.individualName?.message || null}
                    isInvalid={errors?.individualName ? true : false}
                  />
                </div>
                <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='individualPhone'>Phone Number *</Text>
                  <Input
                    {...register('individualPhone')}
                    size='sm'
                    label={<Text>Phone Number</Text>}
                    placeholder={t('0123456789')}
                    errorMessage={errors?.individualPhone?.message || null}
                    isInvalid={errors?.individualPhone ? true : false}
                  />
                </div>
                <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='individualRelationship'>Relationship to enrollee</Text>
                  <Select
                    {...register('individualRelationship')}
                    size='sm'
                    label={<Text>Select</Text>}
                    options={individualRelationships}
                    selectionMode="single"
                    isDisabled={individualRelationships?.length === 0}
                    errorMessage={errors?.individualRelationship?.message || null}
                    isInvalid={errors?.individualRelationship ? true : false}
                  />
                </div>
                <div className='col-span-12 sm:col-span-5 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='individualSignature'>Signature *</Text>
                  <Input
                    {...register('individualSignature')}
                    size='sm'
                    label={<Text>Signature</Text>}
                    placeholder="-"
                    errorMessage={errors?.individualSignature?.message || null}
                    isInvalid={errors?.individualSignature ? true : false}
                  />
                </div>
                <div className='col-span-12 sm:col-span-7 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='individualNPN'>National Producer Number</Text>
                  <Input
                    {...register('individualNPN')}
                    size='sm'
                    label={<Text>(NPN - for agents / brokers only)</Text>}
                    placeholder="-"
                    errorMessage={errors?.individualNPN?.message || null}
                    isInvalid={errors?.individualNPN ? true : false}
                  />
                </div>
                <div className='col-span-12 sm:col-span-4 flex flex-col justify-start items-start'>
                  <Text type='label' htmlFor='individualReceivedDate'>Received Date *</Text>
                  <DateInput 
                    name='individualReceivedDate'
                    label={<Text>Received Date</Text>} 
                    control={control} 
                    errorMessage={errors?.individualReceivedDate?.message || null}
                    isInvalid={errors?.individualReceivedDate ? true : false}
                  />
                </div>
              </div>
            </div>
          </section>)}
          <div className='w-full'>
            <div className="w-full flex flex-row justify-end gap-3 mt-5">
              <Button type='button' color='primary' variant='bordered' radius='full' onClick={() => goToTab('premium')}><Text>Previous</Text></Button>
              <Button type='submit' color='primary' radius='full'><Text>Next</Text></Button>
            </div>
          </div> 
        </form>
      </FormProvider>
    </main>
  );
}

export default SignatureDetails;